import { z } from "zod";
import { exportFileFormat } from "./export-format";
import { isValidCNPJ, isValidCPF, normalizeCep, normalizeDocument, onlyDigits } from "@cx/domain";
import { listQuery } from "./common";
import { ulidParam } from "./ids";
import { blank, num, text } from "./fields";

/** Só http/https com domínio de verdade: recusa javascript:, data: e localhost (DAD6). */
export const httpUrl = z
  .url({
    protocol: /^https?$/,
    hostname: z.regexes.domain,
    error: "Endereço inválido (use http:// ou https://)",
  })
  .max(200);
/** Data ISO que existe no calendário: recusa 2026-13-45 e 2026-02-30 (DAD19). */
export const isoDateValue = z.iso.date("Data inválida");

const email = blank.pipe(z.email("E-mail inválido").max(254).nullable());
const date = blank.pipe(isoDateValue.nullable());

export const addressInput = z.object({
  kind: z.enum(["main", "billing", "delivery"]),
  cep: blank.transform((v, ctx) => {
    if (v === null) return null;
    const n = normalizeCep(v);
    if (!n) {
      ctx.addIssue({ code: "custom", message: "CEP inválido" });
      return z.NEVER;
    }
    return n;
  }),
  uf: blank.pipe(z.string().length(2, "UF inválida").toUpperCase().nullable()),
  city: text(80),
  cityIbge: blank.pipe(
    z
      .string()
      .regex(/^\d{7}$/, "Código IBGE inválido")
      .nullable(),
  ),
  district: text(80),
  street: text(120),
  number: text(20),
  complement: text(80),
  country: z.string().length(2).default("BR"),
});

export const personInput = z.object({
  name: z.string().trim().min(1, "Informe o nome").max(120),
  role: text(60),
  phone: text(30),
  email,
});

/** Teto de valores de campos customizados por contato (o JSON inteiro ainda passa pelo bodyLimit da API). */
export const MAX_CUSTOM_FIELD_VALUES = 200;

export const contactInput = z
  .object({
    code: text(30),
    name: z.string().trim().min(1, "Informe o nome").max(120),
    tradeName: text(120),
    personType: z.enum(["J", "F", "E"]),
    document: blank.pipe(z.string().max(30).nullable()),
    ie: text(20),
    ieIndicator: z.union([z.literal(1), z.literal(2), z.literal(9)]).default(9),
    im: text(20),
    rg: text(20),
    rgIssuer: text(20),
    crt: num(z.number().int().min(0).max(4)),
    customerSince: date,
    phone: text(30),
    fax: text(30),
    mobile: text(30),
    mobileCarrier: text(30),
    email,
    nfeEmail: email,
    website: blank.pipe(httpUrl.nullable()),
    skype: text(60),
    nextVisitAt: date,
    maritalStatus: text(30),
    profession: text(60),
    gender: z
      .enum(["M", "F", "O"])
      .nullish()
      .transform((v) => v ?? null),
    birthDate: date,
    birthplace: text(80),
    fatherName: text(120),
    fatherCpf: blank.pipe(z.string().max(14).nullable()),
    motherName: text(120),
    motherCpf: blank.pipe(z.string().max(14).nullable()),
    avgLoadPct4: num(z.number().int().min(0).max(1_000_000)),
    status: z.enum(["active", "inactive", "no_movement"]).default("active"),
    sellerId: blank.pipe(ulidParam.nullable()),
    finalConsumerNfse: z
      .union([z.literal(0), z.literal(1), z.literal(2)])
      .nullish()
      .transform((v) => v ?? null),
    creditLimitMode: z.enum(["unlimited", "zero", "value"]).default("unlimited"),
    creditLimitCents: num(z.number().int().min(0)),
    notes: z
      .string()
      .max(5000)
      .nullish()
      .transform((v) => (v ? v : null)),
    typeIds: z
      .array(ulidParam)
      .max(10)
      .default([])
      .transform((ids) => [...new Set(ids)]),
    addresses: z.array(addressInput).max(3).default([]),
    persons: z.array(personInput).max(20).default([]),
    customFields: z
      .record(ulidParam, z.string().max(2000))
      .refine((r) => Object.keys(r).length <= MAX_CUSTOM_FIELD_VALUES, {
        message: `No máximo ${MAX_CUSTOM_FIELD_VALUES} campos customizados.`,
      })
      .default({}),
  })
  .superRefine((v, ctx) => {
    // Valida o formato antes de normalizar: "529a982b247c25" não vira um CPF com letras (DAD5).
    if (v.document && v.personType === "F") {
      if (!isValidCPF(v.document))
        ctx.addIssue({ code: "custom", path: ["document"], message: "CPF inválido" });
      else v.document = onlyDigits(v.document);
    } else if (v.document && v.personType === "J") {
      if (!isValidCNPJ(v.document))
        ctx.addIssue({ code: "custom", path: ["document"], message: "CNPJ inválido" });
      else v.document = normalizeDocument(v.document);
    }
    for (const k of ["fatherCpf", "motherCpf"] as const) {
      const cpf = v[k];
      if (!cpf) continue;
      if (!isValidCPF(cpf)) ctx.addIssue({ code: "custom", path: [k], message: "CPF inválido" });
      else v[k] = onlyDigits(cpf);
    }
    if (v.ieIndicator === 1 && !v.ie)
      ctx.addIssue({ code: "custom", path: ["ie"], message: "Contribuinte precisa de Inscrição Estadual" });
    if (v.creditLimitMode === "value" && v.creditLimitCents == null)
      ctx.addIssue({ code: "custom", path: ["creditLimitCents"], message: "Informe o limite de crédito" });
    if (new Set(v.addresses.map((a) => a.kind)).size !== v.addresses.length)
      ctx.addIssue({ code: "custom", path: ["addresses"], message: "Endereço repetido" });
  });

export type ContactInput = z.output<typeof contactInput>;
export type ContactFormValues = z.input<typeof contactInput>;

export const contactListQuery = listQuery.extend({
  /** Id de um tipo de contato, ou "none" para "Sem tipo" (RF-CAD-01, DAD11). */
  type: z.union([z.literal("none"), ulidParam]).optional(),
  status: z.enum(["active", "inactive", "no_movement"]).optional(),
  personType: z.enum(["J", "F", "E"]).optional(),
});
export type ContactListQuery = z.output<typeof contactListQuery>;

/** Filtros aceitos pela exportação (os mesmos da lista, sem paginação). */
export const contactExportInput = contactListQuery
  .pick({
    q: true,
    type: true,
    status: true,
    personType: true,
  })
  .extend({ fileFormat: exportFileFormat });
export type ContactExportInput = z.output<typeof contactExportInput>;

export type ContactListItem = {
  id: string;
  code: string | null;
  name: string;
  tradeName: string | null;
  personType: "J" | "F" | "E";
  document: string | null;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  city: string | null;
  uf: string | null;
  status: "active" | "inactive" | "no_movement";
  types: string[];
};
export type ContactDetail = ContactInput & {
  id: string;
  createdAt: string;
  updatedAt: string;
  types: { id: string; name: string }[];
};
